import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getTodaysDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useCalendarEventsConfigurationShallowStore, useLocationTimezone } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  DatePickerField,
  DropdownField,
  FormRow,
  Tray,
  SwitchField,
  Text,
  TimeField,
  useForm,
  useFormField,
  Button,
  ConfirmationModal,
  useModalControl,
  ContentLoader,
  FormFieldActionTypes,
} from '@frontend/design-system';
import { useDropdownListSelector } from '../../../../components/DropdownListSelector';
import { usePastDateBanner } from '../../../../hooks';
import { getLabelWithTimezoneAbbr } from '../../../../utils';
import { getDateString } from '../../../../utils/date-helpers';
import { EditableTextContainer } from '../../components/EditableTextContainer';
import { EventButton } from '../../components/EventButton';
import { ProviderDropdownListSelector } from '../../components/ProviderDropdownListSelector';
import { CalendarEventsEnums } from '../../types';
import { convertTo24HourFormat, isUUID } from '../../utils';
import { calendarEventsTrackingIds } from './calendar-events-tracking-ids';
import { ExceptionFormValues, OnDeleteExceptionParams } from './types';

interface OutOfOfficeEventsProps {
  eventType: CalendarEventsEnums;
  disableEventType?: boolean; // for V2, change of eventType not allow in update case (for v3, we already handle change of eventType in update case)
  providerList: Provider[];
  selectedLocationIds: string[];
  isSaving: boolean;
  isScheduleV3?: boolean;
  formError: string;
  onSave: (formValues: ExceptionFormValues) => void;
  onDelete: (params: OnDeleteExceptionParams) => void;
  closeModal: () => void;
}

export const OutOfOfficeEventsForm = React.memo(
  ({
    eventType,
    disableEventType,
    formError,
    providerList,
    selectedLocationIds,
    isSaving,
    isScheduleV3,
    onSave,
    onDelete,
    closeModal,
  }: OutOfOfficeEventsProps) => {
    const { t } = useTranslation('scheduleCalendarEvents');
    const { getScopeName } = useAppScopeStore();
    const { configuredCalendarEvent } = useCalendarEventsConfigurationShallowStore('configuredCalendarEvent');

    const [currentEventType, setCurrentEventType] = useState<CalendarEventsEnums>(eventType);

    const textFieldProps = useFormField({ type: 'text', value: configuredCalendarEvent?.name ?? '' });

    const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

    const providerDropdownListSelectorProps = useDropdownListSelector({
      value: isUUID(configuredCalendarEvent?.providerId ?? '') ? configuredCalendarEvent?.providerId : '',
    });

    const locationDropdown = useFormField({
      type: 'dropdown',
      value: selectedLocationIds.length === 1 ? selectedLocationIds[0] : configuredCalendarEvent?.locationId ?? '',
    });
    const filteredProviderList = useMemo(() => {
      if (locationDropdown.value) {
        return providerList.filter((provider) => provider.locationId === locationDropdown.value);
      }
      return [];
    }, [locationDropdown.value, providerList]);

    const { locationTimezoneAbbr } = useLocationTimezone(locationDropdown.value);
    const dateLabel = getLabelWithTimezoneAbbr(t('Date'), locationTimezoneAbbr);
    const timeLabel = getLabelWithTimezoneAbbr(t('Time'), locationTimezoneAbbr);

    const isAllDay = useMemo(
      () =>
        !!(
          configuredCalendarEvent?.startDate &&
          configuredCalendarEvent?.endDate &&
          ((configuredCalendarEvent?.startDate === '00:00' && configuredCalendarEvent?.endDate === '23:59') ||
            (configuredCalendarEvent?.startHour === '12:00 AM' && configuredCalendarEvent?.endHour === '11:59 PM'))
        ),
      [
        configuredCalendarEvent?.startDate,
        configuredCalendarEvent?.endDate,
        configuredCalendarEvent?.startHour,
        configuredCalendarEvent?.endHour,
      ]
    );

    const {
      formProps,
      getFieldProps,
      reset,
      seedValues,
      isComplete: isFormComplete,
      values,
    } = useForm({
      fields: {
        isAllDay: {
          type: 'switch',
          value: isAllDay,
        },
        startDate: {
          type: 'datePicker',
          value: '',
          required: true,
        },
        startTime: {
          type: 'time',
          value: '',
          required: true,
        },
        endDate: {
          type: 'datePicker',
          value: '',
          required: true,
        },
        endTime: {
          type: 'time',
          value: '',
          required: true,
        },
      },
      fieldStateReducer: (state, action) => {
        if (action.type === FormFieldActionTypes.Update || action.type === FormFieldActionTypes.Blur) {
          const startDateValue = typeof state.startDate.value === 'string' ? state.startDate.value : '';
          const endDateValue = typeof state.endDate.value === 'string' ? state.endDate.value : '';
          const startTimeValue = typeof state.startTime.value === 'string' ? state.startTime.value : '';
          const endTimeValue = typeof state.endTime.value === 'string' ? state.endTime.value : '';

          const actionPayloadName = action.payload.name;
          const startDate = dayjs(startDateValue);
          const endDate = dayjs(endDateValue);
          const startTime = dayjs(`2000-01-01 ${startTimeValue}`);
          const endTime = dayjs(`2000-01-01 ${endTimeValue}`);
          const isValidStartAndEndDate = startDate.isValid() && endDate.isValid();
          const isValidStartAndEndTime = startTime.isValid() && endTime.isValid();
          const isInvalidDateRange = startDate.isAfter(endDate, 'day');
          const isInvalidTimeRange = startTime.isAfter(endTime);

          let newState = { ...state };

          if (actionPayloadName === 'endDate' && isValidStartAndEndDate) {
            newState = {
              ...newState,
              endDate: {
                ...newState.endDate,
                'aria-invalid': isInvalidDateRange,
                error: isInvalidDateRange ? t('End date must be after the start date') : '',
              },
            };
          }

          if (actionPayloadName === 'endTime' && isValidStartAndEndTime) {
            newState = {
              ...newState,
              endTime: {
                ...newState.endTime,
                'aria-invalid': isInvalidTimeRange,
                error: isInvalidTimeRange ? t('End time must be after the start time') : '',
              },
            };
          }

          // NOTE: Time field onBlur is returning old value, so using update to handle error
          // Also, updating value directly from state is updating the value but not updating the field visible value
          // @TODO: Implement adding 1hr logic once date field issue is fixed
          // Update only actions
          if (action.type === FormFieldActionTypes.Update) {
            if (actionPayloadName === 'startTime' && isValidStartAndEndTime) {
              newState = {
                ...newState,
                endTime: {
                  ...newState.endTime,
                  'aria-invalid': isInvalidTimeRange,
                  error: isInvalidTimeRange ? t('End time cannot be before start time') : '',
                },
              };
            }
          }

          // Blur only actions
          if (action.type === FormFieldActionTypes.Blur) {
            if (actionPayloadName === 'startDate' && isValidStartAndEndDate) {
              newState = {
                ...newState,
                endDate: {
                  ...newState.endDate,
                  value: isInvalidDateRange ? startDateValue : newState.endDate.value,
                  'aria-invalid': false,
                  error: '',
                },
              };
            }
          }

          // Return the updated state
          return newState;
        }

        return state;
      },

      onSubmit: async (formValues) => {
        const providerId = providerDropdownListSelectorProps.value;

        const locationId = locationDropdown.value
          ? locationDropdown.value
          : providerList.find((provider) => provider.id === providerId)?.locationId ?? '';

        onSave({
          endDate: formValues.endDate || '',
          endTime: formValues.endTime || '',
          startDate: formValues.startDate || '',
          startTime: formValues.startTime || '',
          isAllDay: formValues.isAllDay || false,
          eventId: configuredCalendarEvent?.eventId,
          name: textFieldProps.value,
          providerId,
          locationId,
          eventType: currentEventType,
        });
      },
    });

    const initialScrollStartTime = SchedulerV3.Helpers.useGetTimeFieldStartTime({
      date: values.startDate,
      locationId: locationDropdown.value,
    });

    const handleAddNew = (event?: React.FormEvent<Element>) => {
      if (!event) {
        return;
      }
      formProps.onSubmit(event);
    };

    const handleDeleteAction = () => {
      const { locationId, providerId, eventId } = configuredCalendarEvent ?? {};
      onDelete({
        eventId: eventId ?? '',
        providerId: providerId ?? '',
        locationId: locationId ?? '',
        eventType: currentEventType,
      });
    };

    // useEffect to seed form values when user drags the event in the calendar
    useEffect(() => {
      const hasConfiguredCalendarEvent = !!(
        configuredCalendarEvent?.startHour &&
        configuredCalendarEvent?.endHour &&
        configuredCalendarEvent?.calendarDateValue
      );

      if (hasConfiguredCalendarEvent) {
        seedValues({
          ...values,
          startTime: convertTo24HourFormat(configuredCalendarEvent?.startHour ?? ''),
          endTime: convertTo24HourFormat(configuredCalendarEvent?.endHour ?? ''),
          startDate: dayjs(configuredCalendarEvent?.startDate || configuredCalendarEvent?.calendarDateValue).format(
            'MM/DD/YYYY'
          ),
          endDate: dayjs(configuredCalendarEvent?.endDate || configuredCalendarEvent?.calendarDateValue).format(
            'MM/DD/YYYY'
          ),
        });
      }

      if (configuredCalendarEvent?.providerId) {
        const providerId = isUUID(configuredCalendarEvent?.providerId ?? '') ? configuredCalendarEvent?.providerId : '';
        providerDropdownListSelectorProps.onChange(providerId);
      }
    }, [
      configuredCalendarEvent?.startHour,
      configuredCalendarEvent?.endHour,
      configuredCalendarEvent?.startDate,
      configuredCalendarEvent?.endDate,
      configuredCalendarEvent?.calendarDateValue,
      configuredCalendarEvent?.providerId,
    ]);

    // useEffect to seed form values when user clicks selects all day event
    useEffect(() => {
      if (values.isAllDay) {
        const todaysDate = getTodaysDate('MM/DD/YYYY');
        const startDateString = values.startDate ? values.startDate : configuredCalendarEvent?.startDate || todaysDate;
        const formattedStartDate = dayjs(startDateString).format('MM/DD/YYYY');
        const endDateString = values.endDate ? values.endDate : configuredCalendarEvent?.endDate || todaysDate;
        const formattedEndDate = dayjs(endDateString).format('MM/DD/YYYY');
        seedValues({
          startDate: formattedStartDate,
          startTime: '12:00am',
          endDate: formattedEndDate,
          endTime: '11:59pm',
        });
      }
    }, [values.isAllDay]);

    // Reset form on unmount
    useEffect(() => {
      return () => reset();
    }, []);

    const { pastDateBannerComponent } = usePastDateBanner({
      initialDate: configuredCalendarEvent?.eventId
        ? getDateString(configuredCalendarEvent?.startDate || configuredCalendarEvent?.calendarDateValue)
        : '',
      currentDate: values.startDate,
      locationId: locationDropdown.value,
    });

    const isFormValid =
      isFormComplete &&
      !!textFieldProps.value &&
      (currentEventType === CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT
        ? providerDropdownListSelectorProps.value
        : locationDropdown.value);

    return (
      <>
        <ContentLoader show={isSaving} />
        <div css={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: theme.spacing(2), overflowY: 'auto' }}>
          <Text size='small' color='light'>
            {t('Out of Office')}
          </Text>
          <EditableTextContainer
            name={t('name-your-event')}
            label={t('Name your event')}
            {...textFieldProps}
            placeHolder={t('Name Your Event')}
          />
          <div css={buttonContainerStyles}>
            <EventButton
              disabled={disableEventType}
              isActive={currentEventType === CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT}
              onClick={() => setCurrentEventType(CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT)}
            >
              {t('Provider Event')}
            </EventButton>
            <EventButton
              disabled={disableEventType}
              isActive={currentEventType === CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT}
              onClick={() => setCurrentEventType(CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT)}
            >
              {t('Office Event')}
            </EventButton>
          </div>
          {(isScheduleV3 || currentEventType === CalendarEventsEnums.OFFICE_HOURS_OUT_OF_OFFICE_EVENT) &&
            selectedLocationIds.length > 1 && (
              <div css={[providerFieldContainerStyles, { margin: theme.spacing(3, 0, isScheduleV3 ? 0 : 3, 0) }]}>
                <Text>{t('Location')}</Text>
                <DropdownField
                  name={'location'}
                  css={{ marginLeft: theme.spacing(2) }}
                  label={t('{{count}} Locations', { count: selectedLocationIds.length })}
                  {...locationDropdown}
                >
                  {selectedLocationIds.map((locationId) => {
                    const locationName = getScopeName(locationId);
                    return (
                      <DropdownField.Option key={locationId} value={locationId} searchValue={locationName ?? ''}>
                        <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                          <Icon name='location' size={16} color='light' />
                          <Text>{locationName}</Text>
                        </div>
                      </DropdownField.Option>
                    );
                  })}
                </DropdownField>
              </div>
            )}
          {currentEventType === CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT && (
            <div css={[providerFieldContainerStyles, { margin: theme.spacing(isScheduleV3 ? 0 : 3, 0, 3, 0) }]}>
              <Text>{t('Provider')}</Text>
              <ProviderDropdownListSelector
                providerDropdownListSelectorProps={providerDropdownListSelectorProps}
                listWidth={280}
                filterProviderByProviderUUID={true}
                providersList={isScheduleV3 ? filteredProviderList : providerList}
                selectedLocationIds={selectedLocationIds}
                hideLocationTag={selectedLocationIds.length === 1}
              />
            </div>
          )}
          <form {...formProps}>
            <SwitchField
              {...getFieldProps('isAllDay')}
              label={t('All-day')}
              labelPlacement='left'
              css={{ borderBottom: `1px solid ${theme.colors.neutral20}`, paddingBottom: theme.spacing(1) }}
            />
            <FormRow cols={[100]} css={formRowStyles}>
              <Text css={textStyles}>{t('Start')}</Text>
              <div css={datePickerFieldContainerStyles}>
                <DatePickerField {...getFieldProps('startDate')} label={dateLabel} css={datePickerStyles} />
                <TimeField
                  {...getFieldProps('startTime')}
                  label={timeLabel}
                  css={timeFieldStyles}
                  initialScrollTime={initialScrollStartTime}
                  disabled={values.isAllDay || !values.startDate}
                />
              </div>
            </FormRow>
            <FormRow cols={[100]} css={formRowStyles}>
              <Text css={textStyles}>{t('End')}</Text>
              <div css={datePickerFieldContainerStyles}>
                <DatePickerField
                  {...getFieldProps('endDate')}
                  label={dateLabel}
                  css={datePickerStyles}
                  disabled={!values.startDate || !values.startTime}
                />
                <TimeField
                  {...getFieldProps('endTime')}
                  label={timeLabel}
                  css={timeFieldStyles}
                  initialScrollTime={values.startTime}
                  disabled={values.isAllDay || !values.startDate || !values.endDate}
                />
              </div>
            </FormRow>
          </form>
          {formError && <Text color='error'>{formError}</Text>}
        </div>
        {pastDateBannerComponent}
        <div css={{ display: 'flex', alignItems: 'center' }}>
          {configuredCalendarEvent?.eventId && (
            <Button
              iconName='trash'
              variant='tertiary'
              destructive
              disabled={isSaving}
              onClick={deleteTriggerProps.onClick}
              trackingId={calendarEventsTrackingIds.deleteOutOfOfficeEventButton}
            >
              {t('Delete')}
            </Button>
          )}
          <div css={{ marginLeft: 'auto' }}>
            <Tray.Actions
              primaryLabel={t('Save Event')}
              secondaryLabel={t('Cancel')}
              disabledPrimary={isSaving || !isFormValid}
              disabledSecondary={isSaving}
              onPrimaryClick={handleAddNew}
              onSecondaryClick={closeModal}
              primaryTrackingId={calendarEventsTrackingIds.saveOutOfOfficeEventButton}
              secondaryTrackingId={calendarEventsTrackingIds.cancelOutOfOfficeEventButton}
            />
          </div>
        </div>
        <ConfirmationModal
          {...deleteModalProps}
          title={t('Delete Exception')}
          destructive
          message={t(
            "Are you sure you want to delete {{exceptionName}}? Doing so will remove this exception from {{entityName}}'s schedule, and their availability will be updated accordingly.",
            {
              exceptionName: configuredCalendarEvent?.name || t('exception'),
              entityName:
                eventType === CalendarEventsEnums.PROVIDER_OUT_OF_OFFICE_EVENT
                  ? configuredCalendarEvent?.providerName || t('provider')
                  : getScopeName(configuredCalendarEvent?.locationId || '') || t('location'),
            }
          )}
          confirmLabel={t('Delete')}
          cancelLabel={t('Cancel')}
          onCancel={deleteModalProps.onClose}
          onConfirm={handleDeleteAction}
        />
      </>
    );
  }
);

OutOfOfficeEventsForm.displayName = 'OutOfOfficeEventsForm';

const buttonContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
});

const providerFieldContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(auto, 30%) 1fr',
  alignItems: 'center',
  '> div': {
    width: '100%',
  },
});

const formRowStyles = css({
  margin: theme.spacing(2, 0),
});

const datePickerFieldContainerStyles = css({ display: 'flex', margin: 0, marginLeft: 'auto' });
const datePickerStyles = css({ width: '160px', marginRight: theme.spacing(2) });
const timeFieldStyles = css({ width: '110px' });

const textStyles = css({ margin: 0, marginTop: theme.spacing(1), minWidth: theme.spacing(6) });
